import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fetchChatMedia } from '../../../actions/chatActions';
import { resolveAnyStaticUrl } from '../../../urlConfig';
import axios from '../../../helpers/axios';
import LightboxGallery from '../../../components/media/LightboxGallery';
import VoicePlayer from '../../../components/media/VoicePlayer';

export default function MediaModal({ chatId, type = 'image', onClose }) {
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openLbx, setOpenLbx] = useState(false);
    const [lbxIndex, setLbxIndex] = useState(0);

    const wrapRef = useRef(null);

    const label = {
        image: 'Фотографии',
        video: 'Видео',
        doc: 'Документы',
        voice: 'Голосовые',
        link: 'Ссылки',
    }[type] || 'Медиа';

    const formatName = (u) => {
        if (!u) return 'Неизвестно';
        if (typeof u === 'string') return u;
        const f = u.firstName || '';
        const l = u.lastName || '';
        const name = `${f} ${l}`.trim();
        return name || (u.username ? `@${u.username}` : 'Неизвестно');
    };

    const formatSec = (s) => {
        if (!s && s !== 0) return '';
        const n = Math.max(0, Math.floor(Number(s) / 1000 || Number(s)));
        const mm = String(Math.floor(n / 60)).padStart(2, '0');
        const ss = String(n % 60).padStart(2, '0');
        return `${mm}:${ss}`;
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}.${mm}.${yy}`;
    };

    const load = useCallback(async (before = null) => {
        if (loading) return;
        setLoading(true);
        try {
            const fn = fetchChatMedia(chatId, { type, before, limit: 60 });
            const data = await fn();
            setItems(prev => before ? [...prev, ...(data.items || [])] : (data.items || []));
            setCursor(data.nextCursor || null);
        } catch (e) {
            console.warn('[MediaModal] load error', e?.message || e);
        } finally {
            setLoading(false);
        }
    }, [chatId, type, loading]);

    useEffect(() => { load(null); }, [chatId, type]); // eslint-disable-line

    // подгрузка при скролле
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const onScroll = () => {
            if (loading || !cursor) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
                load(cursor);
            }
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, [cursor, loading, load]);

    // esc для закрытия модалки — не закрываем, если открыт лайтбокс (там свой Esc)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (openLbx) return;
                onClose?.();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, openLbx]);

    // лайтбокс изображения
    const images = (type === 'image')
        ? items.map(it => ({ src: resolveAnyStaticUrl(it.fileUrl), name: it.fileName || '' }))
        : [];

    const openImage = (idx) => {
        setLbxIndex(idx);
        setOpenLbx(true);
    };

    const stop = (e) => { e.stopPropagation(); };

    return (
        <div
            className="mediaModal"
            onClick={() => { if (!openLbx) onClose?.(); }}  // не закрываем фон при открытом лайтбоксе
        >
            <div className="mediaModal__body" onClick={stop}>
                <div className="mediaModal__head">
                    <div className="mediaModal__title">{label}</div>
                    <button className="mediaModal__close" onClick={onClose} title="Закрыть">✕</button>
                </div>

                <div ref={wrapRef} className="mediaModal__content">
                    {/* ФОТО */}
                    {type === 'image' && (
                        <div className="mediaGrid mediaGrid--image">
                            {items.map((it, i) => {
                                const url = resolveAnyStaticUrl(it.fileUrl);
                                return (
                                    <button
                                        key={it._id}
                                        className="mediaCard mediaCard--image"
                                        onClick={() => openImage(i)}
                                        onDoubleClick={(e) => e.stopPropagation()}
                                        title={it.fileName || 'Фото'}
                                    >
                                        <img
                                            className="mediaCard__thumb"
                                            src={url}
                                            alt={it.fileName || 'img'}
                                            onError={() => console.warn('[MediaModal] image error', it.fileUrl)}
                                        />
                                        <div className="mediaCard__overlay">
                                            <span className="mediaCard__sender">{formatName(it.sender)}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* ВИДЕО */}
                    {type === 'video' && (
                        <div className="mediaGrid mediaGrid--video">
                            {items.map((it) => {
                                const url = resolveAnyStaticUrl(it.fileUrl);
                                return (
                                    <div key={it._id} className="mediaCard mediaCard--video" title={it.fileName || 'Видео'}>
                                        <video className="mediaCard__video" src={url} controls preload="metadata" />
                                        <div className="mediaCard__overlay">
                                            <span className="mediaCard__sender">{formatName(it.sender)}</span>
                                            <span className="mediaCard__duration">{formatSec(it.mediaDurationSec)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ДОКУМЕНТЫ */}
                    {type === 'doc' && (
                        <ul className="mediaList mediaList--doc">
                            {items.map((it) => {
                                const url = resolveAnyStaticUrl(it.fileUrl);
                                return (
                                    <li key={it._id} className="mediaList__item">
                                        <a className="mediaDoc" href={url} target="_blank" rel="noreferrer">
                                            <div className="mediaDoc__icon">📎</div>
                                            <div className="mediaDoc__meta">
                                                <div className="mediaDoc__name">{it.fileName || 'Файл'}</div>
                                                <div className="mediaDoc__sub">
                                                    <span className="mediaDoc__sender">{formatName(it.sender)}</span>
                                                    {it.createdAt && <span className="mediaDoc__dot">•</span>}
                                                    {it.createdAt && <span className="mediaDoc__date">{formatDate(it.createdAt)}</span>}
                                                </div>
                                            </div>
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* ГОЛОСОВЫЕ */}
                    {type === 'voice' && (
                        <ul className="mediaList mediaList--voice">
                            {items.map((it) => {
                                const url = resolveAnyStaticUrl(it.fileUrl);
                                return (
                                    <li key={it._id} className="mediaList__item">
                                        <div className="mediaVoice" style={{ alignItems: 'center' }}>
                                            {/* сам плеер: прогресс, перемотка, play/pause, длительность из metadata */}
                                            <div className="mediaVoice__player" style={{ flex: 1 }}>
                                                <VoicePlayer
                                                    src={url}
                                                    filename={it.fileName}
                                                    durationHintSec={Number(it.mediaDurationSec) || 0}
                                                />
                                            </div>

                                            {/* скачать справа */}
                                            <div className="mediaVoice__right" style={{ marginLeft: 12 }}>
                                                <a className="mediaVoice__dl" href={url} target="_blank" rel="noreferrer" title="Скачать">⤓</a>
                                            </div>
                                        </div>

                                        {/* подписи под строкой (кто отправил и дата) — опционально */}
                                        <div style={{ display: 'flex', gap: 8, marginTop: 6, fontSize: 12, color: '#93a4b8' }}>
                                            <span>{formatName(it.sender)}</span>
                                            {it.createdAt && <span>•</span>}
                                            {it.createdAt && <span>{formatDate(it.createdAt)}</span>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}


                    {/* ССЫЛКИ */}
                    {type === 'link' && (
                        <ul className="mediaList mediaList--link">
                            {items.map((it) => {
                                const links = (it.text || '').match(/https?:\/\/\S+|www\.\S+/gi) || [];
                                if (!links.length) return null;
                                return (
                                    <li key={it._id} className="mediaList__item">
                                        <div className="mediaLink">
                                            <div className="mediaLink__sender">{formatName(it.sender)}</div>
                                            {it.createdAt && <div className="mediaLink__date">{formatDate(it.createdAt)}</div>}
                                            <div className="mediaLink__list">
                                                {links.map((u, i) => {
                                                    const href = u.startsWith('http') ? u : ('https://' + u);
                                                    return <a key={i} className="mediaLink__a" href={href} target="_blank" rel="noreferrer">🔗 {u}</a>;
                                                })}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {loading && <div className="mediaModal__hint">Загрузка…</div>}
                    {!loading && !items.length && <div className="mediaModal__hint">Ничего нет</div>}
                </div>
            </div>

            {/* Лайтбокс */}
            <LightboxGallery
                open={openLbx}
                images={images}
                startIndex={lbxIndex}
                onClose={() => setOpenLbx(false)}
            />
        </div>
    );
}
